<template>
  <div class="flex h-full overflow-y-hidden">
    <div class="w-1/4 min-w-64 overflow-visible">
      <app-header-bot-control :bot-id="botId" @change-bot="changeBot"/>
      <list-section :items="answerList"
                    :bot-id="botId"
                    :loaders="loaders"
                    :renamed-id="renamedId"
                    :selected-id="$route.params.answerId"
                    :add-tooltip-text="isNewAnswer ? 'Cancel Answer' : 'Create Answer'"
                    :route-props="{ itemIdName: 'answerId', route: ROUTE_NAMES.answers, name: ROUTE_NAMES.answers }"
                    :menu-builder="menuBuilder"
                    @add="createNewAnswer"
                    @cancel="cancelNewAnswer"
                    @set-rename-mode="handleRenameMode"
                    @rename="saveAnswerName"
                    @item-selected="selectAnswer"
                    @request-delete="handleRequestDelete"
                    @show="showAnswerUsages"
                    @toggle-publish-all-markets="togglePublishAllMarkets"
                    @enable-auto-translate="handleRequestEnableAutoTranslate"
      />
    </div>

    <shadowed-wrapper class="w-3/4">
      <app-header v-if="answer" class="flex justify-between pl-10 pr-5 w-full">
        <app-header-item-control :item="answer"
                                 :is-rename-mode="isRenameModeInAppHeader"
                                 :is-loading="loaders.name"
                                 :menu-builder="menuBuilder"
                                 @set-rename-mode="handleRenameMode"
                                 @rename="saveAnswerName"
                                 @request-delete="handleRequestDelete"
                                 @show="showAnswerUsages"
                                 @toggle-publish-all-markets="togglePublishAllMarkets"
                                 @enable-auto-translate="handleRequestEnableAutoTranslate"
        >
          <template #multi-bot-item-selector>
            <MultiBotItemSelector v-if="answer && answer.multiBotItemId && answerVariantList.length"
              :items="answerVariantList"
              :selected-id="answerId"
              :bot-id="botId"
              :display-published-status="true"
              :is-unknown-answer="isUnknownAnswer"
              editor-type="Answer"
              @item-selected="selectAnswerVariant"
              class="ml-4"
            />
          </template>
        </app-header-item-control>

        <answer-save-button :answer="answer"
                            :bot-id="botId"
                            :is-disabled="inheritsConversation"
                            class="my-auto"
                            @process-new-answer="processNewAnswer"
        />
      </app-header>

      <div class="flex w-full h-full">
        <with-loader id="chat-preview-loader"
                     class="border-transparent bg-white w-7/12 min-w-136 h-full"
                     :loading="!answer || loaders.conversationJsons"
                     trigger-loader-on-mount
        >
          <chat-preview class="flex flex-grow"
                        :customer-input="customerInput"
                        :item-id="answerId"
                        :is-read-only="inheritsConversation"
          />
        </with-loader>

        <with-loader id="editor-sidebar-loader"
                     class="border-transparent bg-white min-w-104 w-5/12 h-full"
                     :loading="!answer || loaders.conversationJsons"
                     trigger-loader-on-mount
        >
          <editor-sidebar :bot-id="botId"
            :item="answer"
            @edit-market="detachAnswer"
          />
        </with-loader>
      </div>
    </shadowed-wrapper>

    <delete-dialog :is-shown="isDeleteDialogShown"
                   :item="itemToDelete"
                   :delete-title="deleteTitle"
                   :delete-description="deleteDescription"
                   :delete-confirm-text="deleteConfirmText"
                   @cancel="cancelDelete"
                   @delete="deleteAnswer"
    >
      <template #delete-item-icon>
        <delete-answer-icon class="h-8 w-8 text-gray-dark"/>
      </template>
    </delete-dialog>

    <confirm-dialog :is-shown="isConfirmDialogShown"
                    :item="itemToEnableAutoTranslate"
                    :title="enableAutoTranslateTitle"
                    :description="enableAutoTranslateDescription"
                    :confirm-text="enableAutoTranslateConfirmText"
                    :busy-button-id="BUSY_BUTTONS.ENABLE_AUTO_TRANSLATE"
                    @cancel="cancelEnableAutoTranslate"
                    @confirm="confirmEnableAutoTranslate"
    >
      <template #icon>
        <vs-icon icon="sync" size="3rem" class="text-gray-dark" />
      </template>
    </confirm-dialog>
  </div>
</template>

<script>
import AppHeader from '@/components/common/AppHeader'
import AppHeaderBotControl from '@/components/common/AppHeaderBotControl'
import AppHeaderItemControl from '@/components/common/AppHeaderItemControl'
import ChatPreview from '@/components/preview/ChatPreview'
import DeleteAnswerIcon from '@/assets/images/icons/DeleteAnswerIcon'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import DeleteDialog from '@/components/common/DeleteDialog.vue'
import EditorSidebar from '@/components/editor/EditorSidebar'
import ListSection from '@/components/common/ListSection.vue'
import ShadowedWrapper from '@/components/base-components/ShadowedWrapper'
import AnswerSaveButton from './AnswerSaveButton'
import head from 'ramda/src/head'
import isEmpty from 'ramda/src/isEmpty'
import conversationModule from '@/store/modules/conversation.module'
import previewModule from '@/store/modules/preview.module'
import answerModule from '@/store/modules/answer.module'
import busyButtonsModule from '@/store/modules/busy-buttons.module'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import intentModule from '@/store/modules/intent.module'
import templateModule from '@/store/modules/template.module'
import answerService from '@/services/amio/answer.service'
import templateService from '@/services/amio/template.service'
import { ROUTE_NAMES } from '@/router'
import { CONVERSATION_TYPES, PREVIEW_ITEM } from '@/store/constants'
import stateTreeUtils from '@/utils/state-tree.utils'
import toaster from '@/utils/toaster'
import { BUSY_BUTTONS } from '@/utils/busy-buttons.constants'
import { ITEM_IDS, ITEM_NAMES, MENU_ITEMS } from '@/utils/editor.constants'
import findIndex from 'ramda/src/findIndex'
import propEq from 'ramda/src/propEq'
import update from 'ramda/src/update'
import tail from 'ramda/src/tail'
import prepend from 'ramda/src/prepend'
import WithLoader from '@/components/common/WithLoader.vue'
import editorUtils from '@/utils/editor.utils'
import errorUtils from '@/utils/error.utils'
import botModule from '@/store/modules/bot.module'
import emailConfigService from '@/services/amio/email-config.service'
import channelModule from '@/store/modules/channel.module'
import { DELETE_TEXT, ENABLE_AUTO_TRANSLATE_TEXT } from '@/utils/text.constants'
import variablesModule from '@/store/modules/variables.module'
import MultiBotItemSelector from '@/components/common/MultiBotItemSelector.vue'

export default {
  name: 'Answer',

  metaInfo() {
    const answerName = this.answer?.name || ''
    return {
      title: [answerName, 'Answers'].join(' | ')
    }
  },

  components: {
    AppHeader,
    AppHeaderBotControl,
    AppHeaderItemControl,
    ChatPreview,
    DeleteAnswerIcon,
    ConfirmDialog,
    DeleteDialog,
    EditorSidebar,
    ListSection,
    ShadowedWrapper,
    AnswerSaveButton,
    WithLoader,
    MultiBotItemSelector
  },

  props: {
    botId: {
      type: String,
      required: true
    }
  },

  watch: {
    botId: {
      handler: async function (botId, previousBotId) {
        if (this.$route.params.answerId === ITEM_IDS.NEW) return

        const currentAnswerMultiBotItemId = this.answer?.multiBotItemId
        await botModule.whenBotsReady()
        await this.loadAnswerList(botId)
        if (isEmpty(this.answerList)) return

        const primaryBotId = botModule.getPrimaryBotId(botId)
        await this.loadTemplateList(primaryBotId)
        const isVariantSwitch = primaryBotId === botModule.getPrimaryBotId(previousBotId)
        if (isVariantSwitch && botId !== primaryBotId) {
          await this.loadTemplateList(botId)
        }
        const answerIdFromVariant = isVariantSwitch && currentAnswerMultiBotItemId ?
          this.answerList.find(answer => answer.multiBotItemId === currentAnswerMultiBotItemId)?.id :
          null
        const answerId = answerIdFromVariant || head(this.answerList).id
        await this.initAnswer(answerId, botId)
        await this.initEmailTemplate()
      }
    },
    '$route.params.answerId': {
      handler: async function (answerId) {
        if (!answerId || answerId === this.answerId) return
        if (answerId === ITEM_IDS.NEW) return
        if (isEmpty(this.answerList)) return

        await botModule.whenBotsReady()
        await this.initAnswer(answerId, this.$route.params.botId)
      }
    },
    '$route.query.sidebar': {
      handler: function (sidebar) {
        if (sidebar === 'usages') {
          answerModule.showUsages()
        } else {
          answerModule.hideUsages()
        }
      }
    }
  },

  data() {
    return {
      answerId: this.$route.params.answerId,
      renamedId: null,
      isRenameModeInAppHeader: false,
      itemToDelete: null,
      loaders: {
        conversationJsons: false,
        name: false,
        items: true
      },
      PREVIEW_ITEM,
      ROUTE_NAMES,
      isDeleteDialogShown: false,
      isConfirmDialogShown: false,
      itemToEnableAutoTranslate: null,
      ITEM_IDS,
      BUSY_BUTTONS,
      pendingAnswerLoad: null
    }
  },

  computed: {
    answer: answerModule.getAnswer,
    answerList: answerModule.getAnswerList,
    answerVariantList: answerModule.getAnswerVariantList,
    bubbleSelected: chatBubbleModule.bubbleSelected,
    isSaved: previewModule.isSaved,
    stateTree: chatBubbleModule.stateTree,
    bot: botModule.getBot,
    botEmailConfig: previewModule.botEmailConfig,
    conversationType: conversationModule.getType,
    intent: intentModule.getIntent,

    isPrimaryVariant() {
      return botModule.isPrimaryVariant(this.botId)
    },

    inheritsConversation() {
      return this.answer?.inheritsConversation || false
    },

    defaultCustomerBubbleText() {
      return '(No answer description)'
    },

    deleteTitle() {
      return DELETE_TEXT[this.conversationType]?.title || ''
    },

    deleteDescription() {
      return DELETE_TEXT[this.conversationType]?.getDescription(this.itemToDelete?.name) || ''
    },

    deleteConfirmText() {
      return DELETE_TEXT[this.conversationType]?.confirm || ''
    },

    botVariantList: botModule.getBotVariantList,

    enableAutoTranslateMarketName() {
      return this.botVariantList.find(botVariant => botVariant.primaryBotId === this.botId)?.name || ''
    },

    enableAutoTranslateTitle() {
      return ENABLE_AUTO_TRANSLATE_TEXT.title
    },

    enableAutoTranslateDescription() {
      const answerName = this.itemToEnableAutoTranslate?.name || ''
      return ENABLE_AUTO_TRANSLATE_TEXT.getDescription(answerName, this.enableAutoTranslateMarketName)
    },

    enableAutoTranslateConfirmText() {
      return ENABLE_AUTO_TRANSLATE_TEXT.confirm
    },

    customerInput() {
      if (!this.answer || this.isNewAnswer) return null

      return this.intent?.description || this.defaultCustomerBubbleText
    },

    isAnswerIdLoaded() {
      return this.answerList.some(({ id }) => id === this.$route.params.answerId)
    },

    isNewAnswer() {
      return this.answer?.id === ITEM_IDS.NEW
    },

    isUnknownAnswer() {
      return editorUtils.isUnknownAnswer(this.answer?.intent.platformId)
    },

    areAllMarketsPublished() {
      const answerVariants = this.answerVariantList || []
      if (!answerVariants.length) return !!this.answer?.published

      return answerVariants.every(answerVariant => answerVariant.published)
    }
  },

  methods: {
    async detachAnswer() {
      busyButtonsModule.setButtonBusy(BUSY_BUTTONS.EDIT_MARKET, true)
      try {
        const updatedAnswer = await answerService.update(this.botId, this.answer.id, {
          inherit_conversation: false,
          conversation_json: this.answer.conversationJson
        })
        this.processAnswer(updatedAnswer)
      } catch (err) {
        toaster.error(err)
      } finally {
        busyButtonsModule.setButtonBusy(BUSY_BUTTONS.EDIT_MARKET, false)
      }
    },

    async attachAnswer(answer) {
      try {
        const updatedAnswer = await answerService.update(this.botId, answer.id, {
          inherit_conversation: true
        })
        this.processAnswer(updatedAnswer)
      } catch (err) {
        toaster.error(err)
      }
    },

    changeBot(bot) {
      this.updateRouteAnswerId(this.answerId, bot.id)
      if (!this.isSaved) return

      botModule.setBot(bot)
      channelModule.setChannelList(bot.id)
    },

    updateRouteAnswerId(answerId, botId = this.$route.params.botId || this.botId) {
      editorUtils.updateRouteByItemId(ITEM_NAMES.ANSWER, { answerId, botId })
    },

    resolveAnswerIdFromRoute() {
      const routeAnswerId = this.$route.params.answerId
      const routeMatch = this.answerList.find(answer => answer.id === routeAnswerId)
      if (routeMatch) return routeMatch.id

      return this.answer?.id || head(this.answerList).id
    },

    selectAnswerVariant(answerId, botId) {
      this.updateRouteAnswerId(answerId, botId)
    },

    // Record which market variant is being viewed so market-scoped variables resolve for
    // that market. We can't use store.state.bot for this — the layout keeps it pinned to
    // the group's primary bot on every route change.
    syncResolutionBot(botId) {
      botModule.setCurrentMarketBotId(botId)
    },

    async showAnswerUsages({ id }) {
      if (this.answer.id !== id) {
        await this.selectAnswer(id)
        this.updateRouteAnswerId(id)
      }

      answerModule.showUsages()
    },

    async handleShowAnswerUsagesClick(answerId) {
      if (this.answer?.id === answerId) {
        // Already on the answer, just show usages
        answerModule.showUsages()
      } else {
        // On a different answer, navigate with query param
        await this.$router.push({
          name: ROUTE_NAMES.answers,
          params: { botId: this.botId, answerId },
          query: { sidebar: 'usages' }
        })
      }
    },

    handleUsagesLinkClick(event) {
      const link = event.target.closest('.show-answer-usages')
      if (!link) return

      event.preventDefault()
      const answerId = link.getAttribute('data-answer-id')
      if (answerId) {
        this.handleShowAnswerUsagesClick(answerId)
      }
    },

    async createNewAnswer() {
      const primaryBotId = botModule.getPrimaryBotId(this.botId)
      const needsMarketSwitch = primaryBotId !== this.botId

      this.updateRouteAnswerId(ITEM_IDS.NEW, primaryBotId)
      if (!this.isSaved) return

      if (needsMarketSwitch) {
        await Promise.all([
          this.loadAnswerList(primaryBotId),
          this.loadTemplateList(primaryBotId)
        ])
      }

      const newIntent = { id: ITEM_IDS.NEW, platformId: '' }
      intentModule.setIntent(newIntent)

      const newAnswer = {
        name: 'New Answer',
        id: ITEM_IDS.NEW,
        published: false,
        intent: newIntent,
        conversationJson: conversationModule.createEmptyJson()
      }
      await this.processAnswer(newAnswer)
      newAnswer.conversationJsons = conversationModule.getJsons()
      answerModule.setAnswerList(prepend(newAnswer, this.answerList))
      this.renamedId = ITEM_IDS.NEW
      stateTreeUtils.selectNode(this.stateTree)
    },

    cancelNewAnswer() {
      if (!this.isSaved) return

      answerModule.setAnswerList(tail(this.answerList))
      const answerId = head(this.answerList).id
      this.selectAnswer(answerId)
    },

    cancelDelete() {
      this.itemToDelete = null
      this.isDeleteDialogShown = false
    },

    handleRequestDelete(item) {
      this.itemToDelete = item
      this.isDeleteDialogShown = true
    },

    handleRequestEnableAutoTranslate(answer) {
      this.itemToEnableAutoTranslate = answer
      this.isConfirmDialogShown = true
    },

    cancelEnableAutoTranslate() {
      this.itemToEnableAutoTranslate = null
      this.isConfirmDialogShown = false
    },

    async confirmEnableAutoTranslate(answer) {
      busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ENABLE_AUTO_TRANSLATE, true)
      try {
        await this.attachAnswer(answer)
      } finally {
        busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ENABLE_AUTO_TRANSLATE, false)
        this.cancelEnableAutoTranslate()
      }
    },

    async deleteAnswer(answer) {
      // hide the dialog right away so it doesn't linger on top of the page while the request runs
      this.isDeleteDialogShown = false
      busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, true)
      try {
        await answerService.delete(this.botId, answer.id)
        const newAnswerId = editorUtils.resolveNewItemId(answer.id, this.answerList)
        answerModule.setAnswerList(this.answerList.filter(({ id }) => id !== answer.id))

        if (newAnswerId) {
          this.answerId = newAnswerId
          await this.selectAnswer(this.answerId)
        } else {
          answerModule.setAnswer(newAnswerId)
          await this.$router.replace({ name: ROUTE_NAMES.answers, params: { botId: this.botId } })
        }

        toaster.success(`Answer "${answer.name}" has been deleted.`)
      } catch (err) {
        const errorMessage = err.response?.data?.details?.errors?.[0]?.message || err.message || ''
        if (errorMessage.includes('Cannot delete answer') && errorMessage.includes('still being used')) {
          const usagesLink = `<br/><a href="#" class="underline text-primary-dark show-answer-usages" data-answer-id="${answer.id}">View answer usages</a>`
          toaster.error(errorMessage, err, {}, null, usagesLink)
        } else {
          errorUtils.handleConversationDesignerError(err, this.botId, this.stateTree, 'Answer', this.$route.fullPath)
        }
      } finally {
        busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, false)
        this.itemToDelete = null
        this.isDeleteDialogShown = false
      }
    },

    async loadAnswerList(botId) {
      this.loaders.items = true

      const dbAnswers = await answerService.list(botId)
      const sortedAnswers = editorUtils.sortAnswerList(dbAnswers)
      answerModule.setAnswerList(sortedAnswers)

      this.loaders.items = false
    },

    menuBuilder(answer) {
      if (editorUtils.isUnknownAnswer(answer?.intent.platformId)) {
        const menu = [MENU_ITEMS.show]
        if (!this.isPrimaryVariant && answer.multiBotItemId && !answer.inheritsConversation) {
          menu.push(MENU_ITEMS.enableAutoTranslate)
        }
        return menu
      }

      const menuOptions = [MENU_ITEMS.rename]
      if (answer.id !== ITEM_IDS.NEW) {
        if (answer.id === this.answer?.id && answer.multiBotItemId && this.answerVariantList.length) {
          menuOptions.push(this.areAllMarketsPublished ? MENU_ITEMS.unpublishAllMarkets : MENU_ITEMS.publishAllMarkets)
        }
        menuOptions.push(MENU_ITEMS.show)
        if (!this.isPrimaryVariant && answer.multiBotItemId && !answer.inheritsConversation) {
          menuOptions.push(MENU_ITEMS.enableAutoTranslate)
        }
        if (this.isPrimaryVariant) menuOptions.push(MENU_ITEMS.delete)
      }

      return menuOptions
    },

    async togglePublishAllMarkets() {
      if (!this.answer?.multiBotItemId || !this.answerVariantList.length) return

      const nextPublishedState = !this.areAllMarketsPublished

      try {
        await editorUtils.updateAnswerVariantsByPublished(nextPublishedState, this.answerVariantList)
        const action = nextPublishedState ? 'published' : 'unpublished'
        toaster.success(`Answer "${this.answer.name}" was ${action} in all markets.`)
      } catch (err) {
        // toaster is handled in editor utils
      }
    },

    async processAnswer(answer, currentBotId = this.botId) {
      if (answer.id === this.answer?.id &&
          answer.conversationJsonHash === this.answer?.conversationJsonHash &&
          answer.inheritsConversation === this.answer?.inheritsConversation) {
        this.loaders.conversationJsons = false
        return
      }
      
      this.loaders.conversationJsons = true
      try {
        conversationModule.setArchivedConversationJson(answer.conversationJson)
        conversationModule.setArchivedIntentDescription(answer.intent.description || null)
        conversationModule.init(CONVERSATION_TYPES.ANSWERS, answer.id, answer.conversationJson, answer.conversationJsonHash)
        const isNewAnswer = answer.id === ITEM_IDS.NEW
        if (!isNewAnswer) {
          intentModule.setIntent(answer.intent)
          if (answer.conversationJson) {
            const botId = botModule.isPrimaryVariant(currentBotId) || answer.inheritsConversation ? botModule.getPrimaryBotId() : currentBotId
            const answerId = botModule.isPrimaryVariant(currentBotId) ? answer.multiBotItemId || answer.id : answer.id
            const referencedTemplates = await answerService.getReferencedConversations(botId, answerId)
            // logic to load template if switching to non primary variant
            await conversationModule.setTemplates(referencedTemplates)
          }
        }

        const answerVariantList = answer.multiBotItemId ?
          await answerService.listAnswerVariants(botModule.getPrimaryBotId(currentBotId), answer.multiBotItemId) :
          []
        answerModule.setAnswerVariantList(answerVariantList)

        this.answerId = answer.id
        answerModule.setAnswer(answer)
        this.loaders.conversationJsons = false
        this.resetBubbleSelected()
      } catch (err) {
        toaster.error(err)
      } finally {
        this.loaders.conversationJsons = false
      }
    },

    async processNewAnswer(answer) {
      answerModule.setAnswerList(update(0, answer, this.answerList))
      await this.processAnswer(answer)
    },

    resetBubbleSelected() {
      if (!this.stateTree) {
        chatBubbleModule.selectBotBubble()
        return
      }

      stateTreeUtils.selectNode(this.stateTree)
    },

    async selectAnswer(answerId) {
      if (this.loaders.conversationJsons || this.renamedId === answerId) return
      if (!this.isSaved || this.answer.id === answerId) return

      await this.initAnswer(answerId)
    },

    async saveAnswerName(updatedAnswer) {
      try {
        this.loaders.name = true
        const index = findIndex(propEq('id', updatedAnswer.id))(this.answerList)
        if (this.answerList[index].name === updatedAnswer.name && !this.isNewAnswer) return

        if (this.isNewAnswer) {
          // The create flow consumes the answer from local state, so it must be
          // set before createAnswer() runs.
          answerModule.setAnswerList(update(index, updatedAnswer, this.answerList))
          answerModule.setAnswer(updatedAnswer)
          const createdAnswer = await editorUtils.createAnswer()
          await this.processNewAnswer(createdAnswer)
          chatBubbleModule.selectCustomerBubble(this.defaultCustomerBubbleText, true)
        } else {
          // Validate the rename succeeded on the backend before mutating local
          // state; otherwise a failed call leaves a stale name on the frontend.
          await answerService.update(this.botId, updatedAnswer.id, { name: updatedAnswer.name })
          answerModule.setAnswerList(update(index, updatedAnswer, this.answerList))
          if (this.answer.id === updatedAnswer.id) {
            answerModule.setAnswer(updatedAnswer)
          }
        }
      } catch (err) {
        toaster.error(err)
      } finally {
        this.resetRenameMode()
        this.loaders.name = false
      }
    },

    resetRenameMode() {
      this.renamedId = null
      this.isRenameModeInAppHeader = false
    },

    async initEmailTemplate() {
      if (!botModule.isEmailBot()) return

      this.botEmailConfig = await emailConfigService.get(this.botId)
    },

    handleRenameMode(e) {
      if (e.fromAppHeader) {
        this.isRenameModeInAppHeader = e.mode
        return
      }

      this.renamedId = e.id
    },

    confirmRouteChange(from) {
      if (isEmpty(this.answerList)) return true
      // set isNewItem to false, because new answer is allowed to save empty
      const wasPreviewChanged = editorUtils.detectPreviewChange(false)
      const wasIntentDescriptionChanged = editorUtils.detectIntentDescriptionChange()
      // confirm() returns true or false based on which button the user has pressed
      if (wasPreviewChanged || wasIntentDescriptionChanged) {
        const userConfirmation = confirm(editorUtils.resolveConfirmText(this.isNewAnswer, 'answer'))
        this.isSaved = userConfirmation
        if (userConfirmation && from.params.answerId === ITEM_IDS.NEW) {
          answerModule.setAnswerList(tail(this.answerList))
        }

        return userConfirmation
      } else {
        this.isSaved = true
        return true
      }
    },

    async initAnswer(answerId, botId = this.$route.params.botId || this.botId) {
      const loadKey = `${botId}:${answerId}`
      if (this.pendingAnswerLoad === loadKey) return // in-flight deduplication guard

      this.pendingAnswerLoad = loadKey
      this.isRenameModeInAppHeader = false
      this.answerId = answerId
      this.syncResolutionBot(botId)

      try {
        this.loaders.conversationJsons = true
        const answer = await answerService.get(botId, answerId)
        await this.processAnswer(answer, botId)
        this.updateRouteAnswerId(answerId, botId)
      } catch (err) {
        toaster.error(err)
      } finally {
        this.loaders.conversationJsons = false
        if (this.pendingAnswerLoad === loadKey) {
          this.pendingAnswerLoad = null
        }
      }
    },

    async loadTemplateList(botId) {
      const templates = await templateService.list(botId)
      templateModule.setTemplateList(templates)
    }
  },

  async mounted() {
    window.addEventListener('beforeunload', e => editorUtils.onBeforeUnload(e))
    document.addEventListener('click', this.handleUsagesLinkClick)

    await botModule.whenBotsReady()

    await Promise.all([
      variablesModule.fetchVariables(),
      this.loadTemplateList(this.botId),
      this.loadAnswerList(this.botId)
    ])
    if (isEmpty(this.answerList)) return

    const answerId = this.resolveAnswerIdFromRoute()
    await this.initAnswer(answerId, this.botId)
    await this.initEmailTemplate()

    if (this.$route.query.sidebar === 'usages') answerModule.showUsages()
  },

  destroyed() {
    window.removeEventListener('beforeunload', e => editorUtils.onBeforeUnload(e))
    document.removeEventListener('click', this.handleUsagesLinkClick)
    botModule.setCurrentMarketBotId(null)
    answerModule.setAnswer(null)
    intentModule.setIntent(null)
    conversationModule.reset()
    conversationModule.resetArchivedConversationJson()
    conversationModule.resetArchivedIntentDescription()
  },

  beforeRouteUpdate(to, from, next) {
    const routeChanged = this.confirmRouteChange(from)
    return routeChanged ? next() : next(false)
  },

  beforeRouteLeave(to, from, next) {
    const routeChanged = this.confirmRouteChange(from)
    return routeChanged ? next() : next(false)
  }
}
</script>
